import React, { useContext } from 'react'
import { countBy, max, omit, pickBy, reduce, toLower } from 'ramda'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { RoomType, RoomUserType } from '../../utils/types'
import { roomDoc, userDoc } from '../../utils/firebase'
import { updateDoc } from 'firebase/firestore'
import Box from '@material-ui/core/Box'
import { ButtonBase, Switch } from '@material-ui/core'
import { DoneOutline, HelpOutline, HighlightOff, Visibility } from '@material-ui/icons'
import { UserContext } from '../UserProvider'
import { InlineEditable } from '../common/InlineEditable'
import cx from 'classnames'

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  item: {
    flex: '0 1 auto',
  },
  list: {
    flex: '1 1 auto',
  },
  marginTop: {
    marginTop: theme.spacing(4),
  },
  icon: {
    display: 'block',
    fontSize: '1.25em',
  },
  deleteIcon: {
    '&:hover': {
      color: theme.palette.error.dark,
    },
  },
  header: {
    marginBottom: '0.5em',
  },
  estimate: {
    fontWeight: 'bold',
  },
  settings: {
    marginBottom: '1em',
  },
  userContainer: {
    flexWrap: 'nowrap',
  },
  user: {
    flex: '1 1 auto',
    overflow: 'hidden',
  },
  username: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  highlight: {
    fontWeight: 'bold',
  },
}))

const Results: React.FC<{ room: RoomType, allUsersHasEstimated: boolean }> = ({ room, allUsersHasEstimated }) => {
  const classes = useStyles()
  const { user: currentUser } = useContext(UserContext)

  const nextRound = () => {
    const newUsers: { [key: string]: RoomUserType } = {}
    Object.values(room.users).forEach(user => newUsers[user.id] = (omit(['estimate'], user)))
    updateDoc(roomDoc(room.id), { users: newUsers })
  }

  const endRound = () => {
    const newUsers: { [key: string]: RoomUserType } = {}
    Object.values(room.users).forEach(user => newUsers[user.id] = { ...user, estimate: user.estimate || '?' })
    updateDoc(roomDoc(room.id), { users: newUsers })
  }

  const mode = () => {
    const estimates = countBy(toLower)(Object.values(room.users).filter(user => !user.observer).map(user => user.estimate || '?'))
    const maxCount = reduce(max, 0, Object.values(estimates))
    const mostCommon = Object.keys(estimates).filter(key => estimates[key] === maxCount)
    return (mostCommon.length === 0 ? ['-'] : mostCommon).join(', ').toUpperCase()
  }

  const removeUser = (userId: string) => {
    const usersAfterRemoval = pickBy((_, key) => key !== userId)(room.users)
    updateDoc(roomDoc(room.id), { users: usersAfterRemoval })
  }

  const editUserName = (userName: string) => {
    if (currentUser && currentUser.name !== userName) {
      updateDoc(userDoc(currentUser.id), { name: userName })
      if (room) {
        updateDoc(roomDoc(room.id), { [`users.${currentUser.id}.name`]: userName })
      }
    }
  }

  const changeObserverMode = () => {
    if (currentUser?.id) {
      const currentMode = room.users[currentUser.id].observer
      updateDoc(roomDoc(room.id), { [`users.${currentUser.id}.observer`]: !currentMode })
    }
  }

  return (
    <Grid container className={classes.container}>
      <Grid item xs={12} className={classes.item}>
        <Typography variant="h5" className={classes.header}>
          Participants:
          {!allUsersHasEstimated && (
            <Typography display="inline" component="span" variant="body2">
              <i> (Waiting for all votes...)</i>
            </Typography>
          )}
        </Typography>
      </Grid>
      {currentUser?.id && (
        <Grid item xs={12} className={cx(classes.item, classes.settings)}>
          <Grid component="label" container alignItems="center" justifyContent="center" spacing={1}>
            <Grid item>Observer</Grid>
            <Grid item>
              <Switch color="primary" checked={!room.users[currentUser.id]?.observer} onChange={changeObserverMode} />
            </Grid>
            <Grid item>Participant</Grid>
          </Grid>
        </Grid>
      )}
      <Grid item xs={12} className={classes.list}>
        {Object.values(room.users).map((user: RoomUserType, index) => (
          <Grid container key={`${user.name}-${index}`} spacing={1} className={classes.userContainer}>
            <Grid item>
              <ButtonBase>
                <HighlightOff className={cx(classes.icon, classes.deleteIcon)} onClick={() => removeUser(user.id)} />
              </ButtonBase>
            </Grid>
            <Grid item className={classes.user}>
              <Typography className={cx(classes.username, { [classes.highlight]: user.id === currentUser?.id })}>
                {user.id !== currentUser?.id ? user.name : (
                  <InlineEditable text={user.name} onSave={editUserName} />
                )}
              </Typography>
            </Grid>
            <Grid item>
              {user.observer && <Visibility className={classes.icon} />}
              {!user.observer && allUsersHasEstimated && (
                <Typography className={classes.estimate}>{user.estimate}</Typography>
              )}
              {!user.observer && !allUsersHasEstimated && user.estimate && <DoneOutline className={classes.icon} />}
              {!user.observer && !allUsersHasEstimated && !user.estimate && <HelpOutline className={classes.icon} />}
            </Grid>
          </Grid>
        ))}

        {Object.keys(room.users).length > 1 && allUsersHasEstimated && (
          <Box mt={2}>
            <Typography>Mode: <b>{mode()}</b></Typography>
          </Box>
        )}
      </Grid>

      <Grid item xs={12} className={classes.item}>
        <Button
          variant={allUsersHasEstimated ? 'contained' : 'outlined'}
          onClick={allUsersHasEstimated ? nextRound : endRound}
          color="primary"
          className={classes.marginTop}
          fullWidth
        >
          {allUsersHasEstimated ? 'Next Round' : 'End round'}
        </Button>
      </Grid>
    </Grid>
  )
}

export default Results
